import React from 'react';
import { createPortal } from 'react-dom';
import { Check, CircleHelp, Euro, MoreVertical, Trash2 } from 'lucide-react';
import { UI_Z_INDEX } from '../../../utils/uiZIndex';
import { useEscapeClose } from '../../../hooks/useEscapeClose';
import { parseHourlyRateOverride } from '../utils/offerLaborPricing';
import { OfferHourlyRatePanel } from './OfferHourlyRateSwitcher';

const MENU_WIDTH = 220;
const VIEWPORT_MARGIN = 8;
const PANEL_GAP = 6;

const decimal = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ICON_BTN =
  'inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-400 hover:bg-blue-50 hover:text-blue-500 disabled:pointer-events-none disabled:opacity-40';
const ICON_BTN_ACTIVE =
  'inline-flex h-7 w-7 items-center justify-center rounded-md border border-blue-200 bg-blue-50 text-blue-600';

export function OfferCalculationRowActions({
  readOnly,
  canDelete,
  deleting,
  onDelete,
  canSetHourlyRate,
  hourlyRateOverride,
  defaultRate,
  onHourlyRateChange,
  canToggleEventual,
  isEventual,
  onToggleEventual,
  rowLabel,
}: {
  readOnly?: boolean;
  canDelete: boolean;
  deleting?: boolean;
  onDelete?: () => void;
  canSetHourlyRate: boolean;
  hourlyRateOverride: number | null;
  defaultRate: number;
  onHourlyRateChange: (next: number | null) => void;
  canToggleEventual: boolean;
  isEventual: boolean;
  onToggleEventual?: () => void;
  rowLabel: string;
}) {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [rateOpen, setRateOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = React.useState<{ top?: number; bottom?: number; left: number } | null>(
    null,
  );
  const override = parseHourlyRateOverride(hourlyRateOverride);
  const hasMenu = canSetHourlyRate || canToggleEventual;
  const menuActive = override != null || isEventual;

  const updateMenuPos = React.useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let left = rect.right - MENU_WIDTH;
    if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;
    if (left + MENU_WIDTH > window.innerWidth - VIEWPORT_MARGIN) {
      left = Math.max(VIEWPORT_MARGIN, window.innerWidth - VIEWPORT_MARGIN - MENU_WIDTH);
    }
    setMenuPos({
      bottom: window.innerHeight - rect.top + PANEL_GAP,
      left,
    });
  }, []);

  React.useLayoutEffect(() => {
    if (!menuOpen) {
      setMenuPos(null);
      return;
    }
    updateMenuPos();
    const onScroll = () => setMenuOpen(false);
    const onResize = () => updateMenuPos();
    document.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [menuOpen, updateMenuPos]);

  React.useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen]);

  useEscapeClose(() => {
    setMenuOpen(false);
    setRateOpen(false);
  }, menuOpen || rateOpen);

  if (!canDelete && !hasMenu) return null;

  return (
    <div className="inline-flex flex-col items-center justify-start gap-0.5">
      {canDelete ? (
        <button
          type="button"
          onClick={onDelete}
          disabled={readOnly || deleting}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-app-text-subtle hover:bg-red-50 hover:text-red-600 disabled:pointer-events-none disabled:opacity-40"
          title="Position entfernen"
          aria-label={`Position ${rowLabel} entfernen`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      ) : null}
      {hasMenu ? (
        <>
          <button
            ref={triggerRef}
            type="button"
            disabled={readOnly}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label={
              override != null
                ? `Weitere Aktionen, abweichender Stundensatz ${decimal.format(override)} Euro pro Stunde`
                : 'Weitere Aktionen'
            }
            title={
              override != null
                ? `Abweichender Stundensatz: ${decimal.format(override)} €/h`
                : 'Weitere Aktionen'
            }
            onClick={() => {
              if (readOnly) return;
              setRateOpen(false);
              setMenuOpen((o) => !o);
            }}
            className={menuActive ? ICON_BTN_ACTIVE : ICON_BTN}
          >
            <MoreVertical className="h-3.5 w-3.5" />
          </button>
          {menuOpen && menuPos
            ? createPortal(
                <div
                  ref={menuRef}
                  role="menu"
                  aria-label="Positionaktionen"
                  style={{
                    position: 'fixed',
                    top: menuPos.top,
                    bottom: menuPos.bottom,
                    left: menuPos.left,
                    width: MENU_WIDTH,
                    zIndex: UI_Z_INDEX.portalDropdown,
                  }}
                  className="rounded-md border border-app-border bg-app-surface py-1 text-app-text shadow-lg"
                >
                  {canSetHourlyRate ? (
                    <button
                      type="button"
                      role="menuitem"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setMenuOpen(false);
                        setRateOpen(true);
                      }}
                      className={
                        'flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-[8.5pt] ' +
                        (override != null
                          ? 'bg-pipedrive-blue/10 font-medium text-pipedrive-blue'
                          : 'text-app-text-muted hover:bg-app-hover')
                      }
                    >
                      <Euro className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      <span className="min-w-0 flex-1">Stundensatz</span>
                      {override != null ? (
                        <span className="tabular-nums">{decimal.format(override)} €/h</span>
                      ) : null}
                    </button>
                  ) : null}
                  {canToggleEventual ? (
                    <button
                      type="button"
                      role="menuitem"
                      aria-checked={isEventual}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        onToggleEventual?.();
                        setMenuOpen(false);
                      }}
                      className={
                        'flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-[8.5pt] ' +
                        (isEventual
                          ? 'bg-pipedrive-blue/10 font-medium text-pipedrive-blue'
                          : 'text-app-text-muted hover:bg-app-hover')
                      }
                    >
                      <CircleHelp className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      <span className="min-w-0 flex-1">
                        {isEventual ? 'Eventualposition aufheben' : 'Eventualposition'}
                      </span>
                      {isEventual ? <Check className="h-3.5 w-3.5 shrink-0" aria-hidden /> : null}
                    </button>
                  ) : null}
                </div>,
                document.body,
              )
            : null}
          <OfferHourlyRatePanel
            open={rateOpen}
            onOpenChange={setRateOpen}
            triggerRef={triggerRef}
            value={hourlyRateOverride}
            defaultRate={defaultRate}
            onChange={onHourlyRateChange}
          />
        </>
      ) : null}
    </div>
  );
}
